import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Reveal } from '../Reveal';
import { SectionBand, Eyebrow } from './SectionBand';
import { Cta } from '../Cta';
import { useScore } from '../ScoreContext';
const faqs = [{
  q: 'Is this book marketing?',
  a: 'No. Book marketing gets more people to the book. Author Leverage builds the path for serious readers after the book creates trust.'
}, {
  q: 'Is this a funnel?',
  a: 'Not in the usual sense. A funnel pushes people toward a predetermined sale. This helps readers take a useful next step based on where they are.'
}, {
  q: 'What if my book has not launched yet?',
  a: 'This can be built before, during, or after launch. The page speaks to authors whose book is already out because that is where the gap is easiest to feel, but pre-launch authors can build the path before readers arrive.'
}, {
  q: 'Will I know exactly who bought my book?',
  a: 'No. Amazon and most retailers do not give you reader data. This gives serious readers a useful reason to raise their hand, so you can see who wants to keep going.'
}, {
  q: 'Do I need a huge audience?',
  a: 'No. If your book supports high-value work, a small number of the right readers taking a useful next step can matter.'
}, {
  q: 'Do you guarantee revenue?',
  a: 'No. The build gives serious readers a clearer path and gives you a better way to see what they may need. Revenue depends on your offer, audience, follow-up, and fit.'
}, {
  q: 'How much time does this take from me?',
  a: 'We do the heavy lifting. We need your judgment, your point of view, and a few focused working sessions to extract the core ideas, review the path, and approve the build.'
}, {
  q: 'What happens after the diagnostic?',
  a: 'You get your Reader-to-Revenue Score. If there is a gap we can help fix, the next step is a Reader Path Audit.'
}, {
  q: 'Do I have to manage the technology?',
  a: 'No. The build is designed to work behind your existing book, site, and offers without turning you into the tech manager.'
}, {
  q: 'Where is the system hosted?',
  a: 'Usually on your own subdomain when that is workable, so it lives with your brand. If speed or technical capacity calls for it, we can host and manage it. Either way the code lives in GitHub as the source of truth.'
}];
function FaqItem({
  question,
  answer
}: {
  question: string;
  answer: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-gray-200" data-id="element-718" data-mp="2d9gu92k1ofot">
      <button type="button" className="group flex w-full items-center justify-between py-5 text-left focus:outline-none" onClick={() => setIsOpen(v => !v)} aria-expanded={isOpen} data-id="element-719" data-mp="2i2069v3332ri">
        <span className="pr-6 font-serif text-lg text-navy transition-colors group-hover:text-seaglass" data-id="element-720" data-mp="28boqnba1f54s">
          {question}
        </span>
        <span className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-paper-dark transition-colors ${isOpen ? 'bg-seaglass/10' : ''}`} data-id="element-721" data-mp="1ucytjvn7x5gf">
          <ChevronDown className={`h-5 w-5 text-text-muted transition-transform duration-300 ${isOpen ? 'rotate-180 text-seaglass' : ''}`} data-id="element-722" data-mp="1foyqnv3fnmjg" />
        </span>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'mb-5 max-h-80 opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-723" data-mp="1as8dbfj0j51q">
        <p className="pr-10 leading-relaxed text-text-muted" data-id="element-724" data-mp="1kns3dlawb9ik">{answer}</p>
      </div>
    </div>;
}
export function FaqFinalCta() {
  const {
    openDiagnostic
  } = useScore();
  return <>
      <SectionBand tone="paper" width="narrow" data-id="element-725" data-mp="1tp9mlcjlnrbh">
        <div className="max-w-3xl" data-id="element-726" data-mp="1togi6i845dwi">
          <Reveal data-id="element-727" data-mp="28dw7dtrzxba3">
            <Eyebrow data-id="element-728" data-mp="28ee1ajr6rdeu">FAQ</Eyebrow>
            <h2 className="mt-4 font-serif text-3xl leading-tight text-navy md:text-4xl" data-id="element-729" data-mp="28dcreilwowyp">
              Frequently asked questions
            </h2>
          </Reveal>
        </div>

        <Reveal delay={0.1} data-id="element-730" data-mp="2hfm0r55fukai">
          <div className="mt-10 border-t border-gray-200" data-id="element-731" data-mp="2heu21lgjo6g0">
            {faqs.map(faq => <FaqItem key={faq.q} question={faq.q} answer={faq.a} data-id="element-732" data-mp="2cgskj4yrof6n" />)}
          </div>
        </Reveal>
      </SectionBand>

      <section className="bg-navy px-6 py-16 text-center text-paper md:px-10 md:py-20" data-id="element-733" data-mp="aq1v6pxxtw92">
        <Reveal data-id="element-734" data-mp="fntwubodwwqg">
          <h2 className="mx-auto max-w-3xl font-serif text-3xl leading-tight text-paper md:text-5xl" data-id="element-735" data-mp="fnafox6k6cdh">
            You already did the hard part.
            <span className="mt-2 block italic text-seaglass-light" data-id="element-736" data-mp="oi8doiokcfbo">
              <span className="block" data-id="element-737" data-mp="3nwtty9wtifw0">Now see whether your readers</span>
              <span className="block" data-id="element-738" data-mp="3nyoevo83a803">have a clear path into your business.</span>
            </span>
          </h2>
          <div className="mt-10 flex flex-col items-center gap-4" data-id="element-739" data-mp="3sy14uhu9bv6w">
            <Cta variant="primary" showArrow onClick={openDiagnostic} className="px-10 py-4 text-lg" data-id="element-740" data-mp="rckbh4glc2b">
              Get Your Reader-to-Revenue Score
            </Cta>
            <p className="text-center text-base font-medium text-paper/70" data-id="element-741" data-mp="1c7svuzcw78jw">
              <span className="block" data-id="element-742" data-mp="1h6duwvgerm6r">8 questions.</span>
              <span className="block" data-id="element-743" data-mp="1h4t0r6y0a59f">No manuscript upload required.</span>
            </p>
          </div>
        </Reveal>
      </section>
    </>;
}